// @flow
import * as React from "react";
import moment from "moment";
import styled, { css, keyframes } from "styled-components";
import Gettext from "../common/Gettext";
import { fontSize, rtl } from "../common/styled-mixins";
// import * as Icon from "./Icons";
// import { Clock } from "../Tournament/IconsTournament";
import { Timer as TimerIcon } from "../Icons/Tournaments";

import type { MapStateToProps } from "react-redux";
import type { State as ReduxState } from "../../redux/reducer";

const Wrapper = styled.div`
  display: flex;
  flex-direction: ${props => props.flexDirection};
  align-items: ${props => props.alignItems};
  font-size: inherit;
  line-height: inherit;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TextView = styled.span`
  color: ${({ theme }) => (process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ? "inherit" : "#cccccc")};
`;

const DirectionContainer = styled.div`
  direction: ${({ theme }) => theme.direction};
`;

const TimeContainer = styled.div`
  margin-top: 5px;
`;

const TimeView = styled.div`
  display: inline;
  font-size: ${fontSize(14, 15)};
  font-weight: ${props => (props.noBold ? 400 : 700)};
  color: inherit;

  ${props =>
    props.isParticipating &&
    css`
      color: ${({ theme }) => theme.tournamentCardSecondaryColor};
    `};

  ${props =>
    props.isCompleted &&
    css`
      color: #99959d;
    `};

  ${({ isTradePage }) =>
    isTradePage &&
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    css`
      color: #feac00;
    `};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  width: ${props => (props.labelOnTop ? "16px" : "27px")};
  ${rtl("margin-right", "5px")};
  color: inherit;

  ${props =>
    props.active &&
    css`
      color: ${({ theme }) => theme.tournamentCardSecondaryColor};
    `};

  ${props =>
    props.isCompleted &&
    css`
      color: #99959d;
    `};

  ${({ isTradePage }) =>
    isTradePage &&
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    css`
      color: #feac00;
    `};
`;

type DurationProps = {
  time?: string,
  start_date: number,
  finish_date: number,
  publish_time: number
};

export const mapStateToTime: MapStateToProps<*, *, *> = (
  state: ReduxState
) => ({
  time: state.time || Date.now()
});

export function getTimes(props: DurationProps) {
  const time = props.time ? moment(props.time) : moment();

  const startTime = moment(props.start_date * 1000);
  const endTime = moment(props.finish_date * 1000);
  const publishTime = moment(props.publish_time * 1000);

  return { time, startTime, endTime, publishTime };
}

export function getActive(props: DurationProps) {
  const { time, startTime, endTime } = getTimes(props);

  return time.isBetween(startTime, endTime);
}

export function getCompleted(props: DurationProps) {
  const { time, endTime } = getTimes(props);

  return time.isAfter(endTime);
}

export function getNew(props: DurationProps) {
  const { time, publishTime } = getTimes(props);

  const diff = time.diff(publishTime, "seconds");
  const seconds = 10800;

  return Math.abs(diff) > seconds ? false : true;
}

export function getDurationObject(props: DurationProps, toStart) {
  const { time, startTime, endTime } = getTimes(props);

  let duration = null;

  if (getActive(props) === true) {
    duration = moment.duration(endTime.diff(time));
  } else if (getCompleted(props) === false && typeof toStart === "undefined") {
    duration = moment.duration(startTime.diff(time));
  } else if (getCompleted(props) === false && typeof toStart === "string") {
    duration = moment.duration(endTime.diff(startTime));
  }

  return duration;
}

export function getDuration(
  props: DurationProps,
  measurement?: "hours" | "days" | "seconds" | "minutes",
  toStart
) {
  const duration = getDurationObject(props, toStart);
  let res = "00";

  if (duration !== null) {
    switch (measurement) {
      case "days":
        res = duration.days();
        break;
      case "hours":
        res = duration.hours();
        break;
      case "minutes":
        res = duration.minutes();
        break;
      case "seconds":
        res = duration.seconds();
        break;
      default:
        res = duration.milliseconds();
    }
  }

  if (isNaN(res)) {
    res = "??";
  }

  return zero(res);
}

function zero(value: string | number) {
  if (typeof value !== "number") {
    return value;
  }

  return value < 10 ? `0${value}` : value;
}

export function compactTimer(props: DurationProps) {
  const duration = getDurationObject(props);
  if (!duration) {
    return "00:00:00";
  }

  const days = duration.days() > 0 ? `${duration.days()}d ` : "";
  const hours = zero(Math.max(0, duration.hours()));
  const minutes = zero(Math.max(0, duration.minutes()));
  const seconds = zero(Math.max(0, duration.seconds()));

  const timer = `${days}${hours}:${minutes}:${seconds}`;

  return timer;
}

type TimeProps = {
  labelOnTop: boolean,
  active: boolean,
  isCompleted: boolean,
  isParticipating?: boolean,
  children: React.Node,
  TimeStyle?: any
};

function Time({
  children,
  labelOnTop,
  active,
  isCompleted,
  isParticipating,
  isTradePage,
  TimeStyle = TimeView
}: TimeProps) {
  return (
    <TimeStyle
      noBold={labelOnTop}
      active={active}
      isCompleted={isCompleted}
      isParticipating={isParticipating}
      isTradePage={isTradePage}
    >
      {children}
    </TimeStyle>
  );
}

type IncompletedProps = {
  labelOnTop: boolean,
  active: boolean,
  isCompleted: boolean,
  isParticipating?: boolean,
  TimeStyle?: any,
  TextStyle?: any,
  days?: number | string,
  hours?: number | string,
  minutes?: number | string,
  seconds?: number | string
};

export function IncompletedTime({ TextStyle, ...props }: IncompletedProps) {
  const precise = parseInt(props.days, 10) === 0;
  const Text = TextStyle || TextView;

  return (
    <React.Fragment>
      {props.days && parseInt(props.days, 10) > 0 && (
        <React.Fragment>
          <Time {...props}>{props.days}</Time>
          &nbsp;
          <Text>
            <Gettext>days</Gettext>
          </Text>
          &nbsp;
        </React.Fragment>
      )}
      <Time isTradePage={props.isTradePage} {...props}>
        {props.hours}
      </Time>
      &nbsp;
      {precise ? (
        <React.Fragment>
          <Text>:</Text>&nbsp;<Time {...props}>{props.minutes}</Time>&nbsp;
          <Text>:</Text>
          &nbsp;
          <Time {...props}>{props.seconds}</Time>
        </React.Fragment>
      ) : (
        <Text>
          <Gettext>hours</Gettext>
        </Text>
      )}
    </React.Fragment>
  );
}

type Props = {
  className?: string,
  flexDirection?: "column" | "row",
  alignItems?: string,
  labelOnTop: boolean,
  small?: boolean,
  noLabel?: boolean,
  noIcon?: boolean,
  active?: boolean,
  isCompleted?: boolean,
  isParticipating?: boolean,
  completedTimer?: moment$Moment | React.Node,
  toStart?: boolean,

  TextStyle?: any,
  TimeStyle?: any,

  days?: number | string,
  hours?: number | string,
  minutes?: number | string,
  seconds?: number | string,
  start_date?: number,
  finish_date?: number,
  tournamentPage?: boolean
};

const opacity = keyframes`
  20%, 50% {opacity:0;}
  50% {display: none;}
  80%, 100% {opacity:1; display: block;}
`;

const ToStart = styled.span`
  position: absolute;
  height: 14px;
  display: inline-block;
  animation-name: ${opacity};
  animation-duration: 8s;
  animation-iteration-count: infinite;

  ${({ center }) =>
    center &&
    css`
      display: block;
      width: 100%;
      left: 0;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      color: ${({ theme }) => theme.tournamentCardSecondaryColor};

      @media (max-width: 359px) {
        font-size: 14px;
      }
    `};

  ${({ date }) =>
    date &&
    css`
      font-size: 14px;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.5) !important;
    `};
`;

const Duration = styled.span`
  position: absolute;
  height: 14px;
  display: inline-block;
  animation-name: ${opacity};
  animation-duration: 8s;
  animation-delay: 4s;
  animation-iteration-count: infinite;

  ${({ center }) =>
    center &&
    css`
      display: block;
      width: 100%;
      left: 0;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      color: ${({ theme }) => theme.tournamentCardSecondaryColor};

      @media (max-width: 359px) {
        font-size: 14px;
      }
    `};

  ${({ date }) =>
    date &&
    css`
      font-size: 14px;
      font-weight: 400 !important;
      color: rgba(255, 255, 255, 0.5) !important;
    `};
`;

const TextHack = styled.div`
  height: 0 !important;
  color: transparent !important;
  user-select: none !important;
`;

const InfoText = styled.span`
  font-size: 14px;
  font-weight: 400 !important;
  color: rgba(255, 255, 255, 0.5) !important;
`;

const TimerContainer = styled.div`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
`;

const TimerInner = styled.div`
  display: flex;
  margin-left: 8px;
`;

const TimerText = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #ffcc00;
`;

const CurrentText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.tournamentCardSecondaryColor};
  text-align: center;

  @media (max-width: 359px) {
    font-size: 14px;
  }
`;

export default function Timer({
  flexDirection = "column",
  alignItems = "start",
  className,
  isTradePage,
  TextStyle = TextView,
  ...props
}: Props) {
  const label =
    props.noLabel === true ? null : (
      <TextStyle
        style={{
          marginRight: flexDirection === "row" ? 8 : 0,
          marginBottom: props.labelOnTop && flexDirection !== "row" ? 8 : 0
        }}
      >
        {props.isCompleted ? (
          <Gettext>The tournament is finished</Gettext>
        ) : props.active ? (
          props.tournamentPage ? (
            <CurrentText>
              <Gettext>The tournament will finish on:</Gettext>
            </CurrentText>
          ) : (
            <span>
              <Gettext>Time left until closing</Gettext>:
            </span>
          )
        ) : !props.toStart ? (
          <span>
            <Gettext>The tournament will start in</Gettext>:
          </span>
        ) : (
          <div style={{ minHeight: 14 }}>
            <ToStart center={props.tournamentPage}>
              {props.tournamentPage ? (
                <span>
                  <Gettext>The tournament will start on:</Gettext>
                </span>
              ) : (
                <span>
                  <Gettext>The tournament will start in</Gettext>:
                </span>
              )}
            </ToStart>
            <Duration center={props.tournamentPage}>
              <span>
                <Gettext>Duration</Gettext>:
              </span>
            </Duration>
            {!props.tournamentPage && (
              <TextHack>
                <div>
                  <Gettext>The tournament will start in</Gettext>:
                </div>
                <div>
                  <Gettext>Duration</Gettext>:
                </div>
              </TextHack>
            )}
          </div>
        )}
      </TextStyle>
    );

  return (
    <Wrapper
      className={className}
      flexDirection={flexDirection}
      alignItems={alignItems}
    >
      {props.labelOnTop ? label : null}
      <Container>
        {props.noIcon ? null : (
          <IconContainer
            active={props.active}
            isCompleted={props.isCompleted}
            labelOnTop={props.labelOnTop}
            isTradePage={isTradePage}
          >
            <TimerIcon width={props.small ? "16px" : "27px"} />
          </IconContainer>
        )}
        <DirectionContainer>
          {props.labelOnTop ? null : label}
          <TimeContainer
            style={{
              minHeight: 14,
              marginTop:
                props.noLabel === true || props.labelOnTop === true
                  ? 0
                  : props.tournamentPage
                  ? 11
                  : 5
            }}
          >
            {props.isCompleted && props.completedTimer ? (
              <Time {...props}>{props.completedTimer}</Time>
            ) : props.toStart && !props.active ? (
              <div style={{ minHeight: 14 }}>
                <ToStart
                  center={props.tournamentPage}
                  date={props.tournamentPage}
                >
                  {props.tournamentPage ? (
                    moment(props.start_date * 1000).format("DD MMM HH:mm")
                  ) : (
                    <IncompletedTime isTradePage={isTradePage} {...props} />
                  )}
                </ToStart>
                <Duration
                  center={props.tournamentPage}
                  date={props.tournamentPage}
                >
                  <IncompletedTime
                    {...props}
                    days={getDuration(props, "days", "toStart")}
                    hours={getDuration(props, "hours", "toStart")}
                    minutes={getDuration(props, "minutes", "toStart")}
                    seconds={getDuration(props, "seconds", "toStart")}
                    active={props.active}
                    isCompleted={props.isCompleted}
                    labelOnTop={props.labelOnTop}
                    isTradePage={isTradePage}
                    TimeStyle={props.tournamentPage && InfoText}
                    TextStyle={props.tournamentPage && InfoText}
                  />
                </Duration>
              </div>
            ) : props.tournamentPage ? (
              <div style={{ height: 14, marginTop: -3, textAlign: "center" }}>
                <InfoText>
                  {moment(props.finish_date * 1000).format("DD MMM HH:mm")}
                </InfoText>
              </div>
            ) : (
              <IncompletedTime
                isTradePage={isTradePage}
                TextStyle={TextStyle}
                {...props}
              />
            )}
          </TimeContainer>
        </DirectionContainer>
      </Container>
      {props.tournamentPage && (
        <TimerContainer>
          <TimerIcon width="16px" />
          <TimerInner>
            <IncompletedTime
              isTradePage={isTradePage}
              {...props}
              TimeStyle={TimerText}
              TextStyle={TimerText}
            />
          </TimerInner>
        </TimerContainer>
      )}
    </Wrapper>
  );
}



// WEBPACK FOOTER //
// ./src/components/Tournaments/Timer.js